import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import di from "../../../../../di/DependencyInjection";
import CarPeritajeReportEntity from "../../../../../domain/entities/CarPeritajeReportEntity";
import GetCarsPeritajeReportUseCase, {
    GetCarsPeritajeReportUseCaseName,
} from "../../../../../domain/use_cases/car/GetCarsPeritajeReportUseCase";
import Layout from "../../../layout/Layout";
import { buildPeritajeCsv, downloadCsv, peritajeCsvFileName } from "./reportePeritajeCsv";
import "./ReportePeritajePageStyles.scss";

type Estado = "cargando" | "listo" | "error";

/**
 * Descarga del listado de vehículos activos con su estado de peritaje.
 *
 * La página arma el reporte al entrar y dispara la descarga sola: la ruta existe para bajar el
 * archivo, no para consultarlo en pantalla. Igual queda el resumen y el botón por si el navegador
 * bloquea la descarga automática o se quiere repetir sin recargar.
 */
const ReportePeritajePage: FC = () => {
    const [estado, setEstado] = useState<Estado>("cargando");
    const [progreso, setProgreso] = useState({ done: 0, total: 0 });
    const [cars, setCars] = useState<CarPeritajeReportEntity[]>([]);
    // StrictMode monta dos veces en desarrollo y el reporte son cientos de peticiones.
    const generando = useRef(false);

    const descargar = useCallback((filas: CarPeritajeReportEntity[]) => {
        downloadCsv(peritajeCsvFileName(), buildPeritajeCsv(filas, window.location.origin));
    }, []);

    const generar = useCallback(async () => {
        if (generando.current) return;
        generando.current = true;
        setEstado("cargando");
        setProgreso({ done: 0, total: 0 });

        try {
            const filas = await di
                .get<GetCarsPeritajeReportUseCase>(GetCarsPeritajeReportUseCaseName)
                .call((done, total) => setProgreso({ done, total }));

            setCars(filas);
            setEstado("listo");
            descargar(filas);
        } catch (error) {
            setEstado("error");
        } finally {
            generando.current = false;
        }
    }, [descargar]);

    useEffect(() => {
        generar();
    }, [generar]);

    const conPeritaje = cars.filter((car) => car.source != null).length;
    const conError = cars.filter((car) => car.checkFailed).length;
    const porcentaje = progreso.total ? Math.round((progreso.done / progreso.total) * 100) : 0;

    return (
        <div className="reporte_peritaje_page">
            <Layout>
                <Helmet>
                    <title>Listado de vehículos activos con peritaje - wcar</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <div className="wrapper">
                    <div className="content">
                        <h1>Vehículos activos y peritaje</h1>

                        {estado === "cargando" && (
                            <>
                                <p>
                                    Consultando el inventario y el peritaje de cada vehículo. Puede tardar un par de
                                    minutos.
                                </p>
                                <div className="progress_bar" role="progressbar" aria-valuenow={porcentaje}>
                                    <div className="progress_bar__fill" style={{ width: `${porcentaje}%` }} />
                                </div>
                                <span className="progress_label">
                                    {progreso.total
                                        ? `${progreso.done} de ${progreso.total} vehículos`
                                        : "Cargando inventario…"}
                                </span>
                            </>
                        )}

                        {estado === "listo" && (
                            <>
                                <p>La descarga debió iniciar sola. Si no, vuelve a descargar el archivo.</p>
                                <ul className="resumen">
                                    <li>
                                        <strong>{cars.length}</strong> vehículos activos
                                    </li>
                                    <li>
                                        <strong>{conPeritaje}</strong> con peritaje
                                    </li>
                                    <li>
                                        <strong>{cars.length - conPeritaje - conError}</strong> sin peritaje
                                    </li>
                                    {conError > 0 && (
                                        <li>
                                            <strong>{conError}</strong> con error de consulta (revisar)
                                        </li>
                                    )}
                                </ul>
                                <button type="button" className="btn btn_orange" onClick={() => descargar(cars)}>
                                    Descargar de nuevo
                                </button>
                            </>
                        )}

                        {estado === "error" && (
                            <>
                                <p>No se pudo armar el listado. Revisa la conexión e inténtalo otra vez.</p>
                                <button type="button" className="btn btn_orange" onClick={generar}>
                                    Reintentar
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Layout>
        </div>
    );
};

export default ReportePeritajePage;
